import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Input,
  VStack,
  HStack,
  Text,
  Avatar,
  InputGroup,
  InputLeftElement,
  Spinner,
  useColorModeValue,
  Flex,
  Badge,
} from '@chakra-ui/react';
import { FiSearch, FiUser } from 'react-icons/fi';

export interface User {
  id: string;
  name?: string;
  surname?: string;
  email?: string;
  phone_number?: string;
  role?: string;
  photo?: string;
}

interface UserAutocompleteProps {
  users: User[];
  value?: string;
  onChange: (userId: string, user?: User) => void;
  placeholder?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  maxResults?: number;
}

const UserAutocomplete: React.FC<UserAutocompleteProps> = ({
  users,
  value,
  onChange,
  placeholder = "Search for users...",
  isLoading = false,
  isDisabled = false,
  error,
  size = 'md',
  maxResults = 10,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');

  // Filter users based on search query
  const filteredUsers = React.useMemo(() => {
    if (!searchQuery.trim()) return users.slice(0, maxResults);
    
    const query = searchQuery.toLowerCase();
    return users
      .filter(user => {
        const fullName = `${user.name || ''} ${user.surname || ''}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        const phoneNumber = (user.phone_number || '').toLowerCase();
        return fullName.includes(query) || email.includes(query) || phoneNumber.includes(query);
      })
      .slice(0, maxResults);
  }, [users, searchQuery, maxResults]);

  // Find selected user when value changes
  useEffect(() => {
    if (value) {
      const user = users.find(u => u.id === value);
      if (user) {
        setSelectedUser(user);
        setSearchQuery(`${user.name || ''} ${user.surname || ''}`.trim() || user.email || '');
      }
    } else {
      setSelectedUser(null);
      setSearchQuery('');
    }
  }, [value, users]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowDropdown(true);
    setHighlightedIndex(-1);
    
    // Clear selection if user is typing
    if (selectedUser && query !== `${selectedUser.name || ''} ${selectedUser.surname || ''}`.trim()) {
      setSelectedUser(null);
      onChange('');
    }
  };

  // Handle user selection
  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setSearchQuery(`${user.name || ''} ${user.surname || ''}`.trim() || user.email || '');
    setShowDropdown(false);
    setHighlightedIndex(-1);
    onChange(user.id, user);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || filteredUsers.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredUsers.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredUsers.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredUsers.length) {
          handleUserSelect(filteredUsers[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle input focus
  const handleFocus = () => {
    if (!isDisabled) {
      setShowDropdown(true);
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getUserDisplayText = (user: User) => {
    const fullName = `${user.name || ''} ${user.surname || ''}`.trim();
    return fullName || user.email || 'Unknown User';
  };

  const getUserSubtext = (user: User) => {
    const fullName = `${user.name || ''} ${user.surname || ''}`.trim();
    return fullName ? user.email : user.phone_number;
  };

  return (
    <Box position="relative" width="100%">
      <InputGroup size={size}>
        <InputLeftElement pointerEvents="none">
          {isLoading ? (
            <Spinner size="sm" color="gray.400" />
          ) : (
            <FiSearch color="gray.400" />
          )}
        </InputLeftElement>
        <Input
          ref={inputRef}
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          isDisabled={isDisabled}
          isInvalid={!!error}
          bg={bg}
          _focus={{
            borderColor: 'blue.500',
            boxShadow: `0 0 0 1px var(--chakra-colors-blue-500)`,
          }}
        />
      </InputGroup>

      {showDropdown && !isDisabled && (
        <Box
          ref={dropdownRef}
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={1000}
          bg={bg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="md"
          boxShadow={`0 4px 6px ${shadowColor}`}
          maxHeight="300px"
          overflowY="auto"
          mt={1}
        >
          {filteredUsers.length === 0 ? (
            <Box p={4} textAlign="center" color="gray.500">
              <FiUser size={20} style={{ margin: '0 auto 8px' }} />
              <Text fontSize="sm">
                {searchQuery.trim() ? 'No users found' : 'Start typing to search...'}
              </Text>
            </Box>
          ) : (
            <VStack spacing={0} align="stretch">
              {filteredUsers.map((user, index) => (
                <Box
                  key={user.id}
                  p={3}
                  cursor="pointer"
                  bg={highlightedIndex === index ? hoverBg : 'transparent'}
                  _hover={{ bg: hoverBg }}
                  onClick={() => handleUserSelect(user)}
                  borderBottom={index < filteredUsers.length - 1 ? '1px solid' : 'none'}
                  borderColor={borderColor}
                >
                  <HStack spacing={3}>
                    <Avatar
                      size="sm"
                      name={getUserDisplayText(user)}
                      src={user.photo}
                    />
                    <Flex direction="column" flex={1} minWidth={0}>
                      <Text fontWeight="medium" fontSize="sm" isTruncated>
                        {getUserDisplayText(user)}
                      </Text>
                      {getUserSubtext(user) && (
                        <Text fontSize="xs" color="gray.500" isTruncated>
                          {getUserSubtext(user)}
                        </Text>
                      )}
                    </Flex>
                    {user.role && (
                      <Badge
                        size="sm"
                        colorScheme={
                          user.role === 'SYSTEM' ? 'red' :
                          user.role === 'TENANT_ADMIN' ? 'blue' : 'gray'
                        }
                        fontSize="xs"
                      >
                        {user.role.replace('_', ' ')}
                      </Badge>
                    )}
                  </HStack>
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      )}
    </Box>
  );
};

export default UserAutocomplete; 